import { ApiError, handleApiError } from '@/handleApiError'
import api from '@/modules/product/api'
import { ProductSearchOptions } from '@/modules/product/types'
import useRealtimePrices from '@/modules/product/useRealtimePrices'
import { PAGE_SIZE } from '@/types'
import { BatchProductPriceUpdate, MediumProduct, ProductPriceUpdate } from '@/types/index'
import { concat } from 'lodash-es'
import { defineStore } from 'pinia'

interface ProductSearchState {
  loading: number
  items: MediumProduct[]
  page: number
  hasNext: boolean
  options: ProductSearchOptions | undefined
  loadingNextPage: boolean
  keywordTooShortError: boolean
}

const initialState = (): ProductSearchState => {
  return {
    loading: 0,
    items: [],
    page: 0,
    hasNext: false,
    options: undefined,
    loadingNextPage: false,
    keywordTooShortError: false
  }
}

const useProductSearch = defineStore('productSearch', {
  state: () => initialState(),
  actions: {
    setOptions(options: ProductSearchOptions | undefined) {
      this.options = options
    },
    setKeywordTooShortError(value: boolean) {
      this.keywordTooShortError = value
    },
    async searchProducts(options: ProductSearchOptions) {
      if (!options.keyword || options.keyword.length > 1) {
        this.loading++
        this.keywordTooShortError = false
        this.options = options
        this.page = 0

        return api
          .searchProducts(options, this.page)
          .then(({ data }) => {
            this.items = data.items
            this.hasNext = data.items.length == (options.size ?? PAGE_SIZE)

            useRealtimePrices().loadRealtimePricesBatch({
              scale: 1,
              productIds: data.items.map((item) => item.productId),
              filters: options.filters
            })
          })
          .catch((error: ApiError) => {
            this.items = []
            this.hasNext = false
            this.page = 1
            handleApiError(error)
          })
          .finally(() => {
            this.loading--
          })
      } else {
        this.keywordTooShortError = true
        this.items = []
        this.hasNext = false
        this.page = 1
      }
    },
    async loadNextPage() {
      if (this.options) {
        if (this.hasNext) {
          this.loadingNextPage = true
          this.page++

          return api
            .searchProducts(this.options, this.page)
            .then(({ data }) => {
              this.items = concat(this.items, data.items)
              this.hasNext = data.items.length == (this.options?.size ?? PAGE_SIZE)

              useRealtimePrices().loadRealtimePricesBatch({
                scale: 1,
                productIds: data.items.map((item) => item.productId),
                filters: this.options?.filters
              })
            })
            .catch((error: ApiError) => {
              this.items = []
              this.hasNext = false
              handleApiError(error)
            })
            .finally(() => {
              this.loadingNextPage = false
            })
        }
      }
    },
    updateProduct(update: ProductPriceUpdate | BatchProductPriceUpdate) {
      const index = this.items.findIndex((p) => p.productId === update.productId)
      const item = this.items[index]
      if (item) {
        item.price = update.price
        item.stock = update.stock
        const suppliers = (update as ProductPriceUpdate).suppliers
        if (suppliers) item.suppliers = suppliers
      }
    }
  }
})

export default useProductSearch
